/**
 * The views of the ticket panel's help (#1172): the "Ähnliche Tickets
 * gefunden" box under the title, the ticket a draft is appended to, and the
 * type/category hint. Pure presentation; the panel owns the state.
 */
import { useTranslation } from 'react-i18next'
import { Sparkles, X } from 'lucide-react'
import { BUG_REPORT_STATUS_COLORS } from '@/components/bug-reports/statusColors'
import type { SimilarTicket } from '@/services/bug-report-service'
import { FOCUS_RING, STATUS, readable } from '../notifications/notificationStyles'
import type { SuggestionHint } from './ticketAssist'

const WARNING_TINT = 'color-mix(in srgb, var(--cdx-color-warning) 9%, var(--window-background))'
const WARNING_BORDER = 'color-mix(in srgb, var(--cdx-color-warning) 45%, var(--window-border-color))'
const ACCENT_TINT = 'color-mix(in srgb, var(--theme-interactive-color) 10%, var(--window-background))'
const ACCENT_BORDER = 'color-mix(in srgb, var(--theme-interactive-color) 40%, var(--window-border-color))'
const SMALL_BUTTON = `h-7 shrink-0 rounded-md border border-[var(--window-border-color)] bg-[var(--window-background)] px-2.5 text-xs font-medium text-[var(--text-color)] hover:bg-[var(--menu-item-hover)] ${FOCUS_RING}`

function StatusDot({ ticket }: { ticket: SimilarTicket }) {
  return (
    <span
      className="h-2 w-2 shrink-0 rounded-full"
      style={{ background: BUG_REPORT_STATUS_COLORS[ticket.status] }}
      aria-hidden
    />
  )
}

function useTicketMeta() {
  const { t } = useTranslation()
  return (ticket: SimilarTicket) =>
    [
      t(`bugReport.type.${ticket.type}`),
      ticket.status === 'closed' && ticket.close_reason
        ? `${t('bugReport.status.closed')} (${t(`bugReport.closeReason.${ticket.close_reason}`)})`
        : t(`bugReport.status.${ticket.status}`),
      ticket.own ? t('ticketPanel.similar.own') : ticket.client_name || t('ticketPanel.similar.noClient'),
    ].join(' · ')
}

export function SimilarTicketsBox({
  tickets,
  canOpen,
  onOpen,
  onAppend,
}: {
  tickets: SimilarTicket[]
  /** Whether this host has a view for the ticket — someone else's ticket may have none (#1191). */
  canOpen: (ticket: SimilarTicket) => boolean
  onOpen: (ticket: SimilarTicket) => void
  onAppend: (ticket: SimilarTicket) => void
}) {
  const { t } = useTranslation()
  const meta = useTicketMeta()
  return (
    <section
      aria-labelledby="ticket-panel-similar-heading"
      data-testid="ticket-panel-similar"
      className="flex flex-col gap-2 rounded-[10px] border px-3 py-2.5"
      style={{ background: WARNING_TINT, borderColor: WARNING_BORDER }}
    >
      <h3 id="ticket-panel-similar-heading" className="text-[13px] font-medium" style={{ color: readable(STATUS.warning) }}>
        {t('ticketPanel.similar.title')}
      </h3>
      <ul className="flex flex-col gap-2">
        {tickets.map((ticket) => (
          <li key={ticket.id} className="flex items-center gap-2.5 text-[13px]" data-testid="ticket-panel-similar-item">
            <StatusDot ticket={ticket} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[var(--text-color)]" title={ticket.title}>
                {ticket.title}
              </span>
              <span className="block truncate text-xs text-[var(--text-secondary)]">{meta(ticket)}</span>
            </span>
            {canOpen(ticket) && (
              <button
                type="button"
                className={SMALL_BUTTON}
                onClick={() => onOpen(ticket)}
                aria-label={t('ticketPanel.similar.openLabel', { title: ticket.title })}
                data-testid={`ticket-panel-similar-open-${ticket.id}`}
              >
                {t('ticketPanel.similar.open')}
              </button>
            )}
            {ticket.own && ticket.can_append && (
              <button
                type="button"
                className={SMALL_BUTTON}
                onClick={() => onAppend(ticket)}
                aria-label={t('ticketPanel.similar.appendLabel', { title: ticket.title })}
                data-testid={`ticket-panel-similar-append-${ticket.id}`}
              >
                {t('ticketPanel.similar.append')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

export function AppendTargetCard({ ticket, onCancel }: { ticket: SimilarTicket; onCancel: () => void }) {
  const { t } = useTranslation()
  const meta = useTicketMeta()
  return (
    <div
      data-testid="ticket-panel-append-target"
      className="flex shrink-0 flex-col gap-2 rounded-[10px] border px-3 py-2.5"
      style={{ background: ACCENT_TINT, borderColor: ACCENT_BORDER }}
    >
      <p className="text-xs text-[var(--text-secondary)]">{t('ticketPanel.append.hint')}</p>
      <div className="flex items-center gap-2.5 text-[13px]">
        <StatusDot ticket={ticket} />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-[var(--text-color)]" title={ticket.title}>
            {ticket.title}
          </span>
          <span className="block truncate text-xs text-[var(--text-secondary)]">{meta(ticket)}</span>
        </span>
        <button type="button" className={SMALL_BUTTON} onClick={onCancel} data-testid="ticket-panel-append-cancel">
          {t('ticketPanel.append.cancel')}
        </button>
      </div>
    </div>
  )
}

export function SuggestionHintRow({
  hint,
  onApply,
  onDismiss,
}: {
  hint: SuggestionHint
  onApply: () => void
  onDismiss: () => void
}) {
  const { t } = useTranslation()
  const category = hint.category ? t(`bugReport.category.${hint.category}`) : null
  const text =
    hint.kind === 'type'
      ? category
        ? t(`ticketPanel.suggestion.type.${hint.type}WithCategory`, { category })
        : t(`ticketPanel.suggestion.type.${hint.type}`)
      : t('ticketPanel.suggestion.category', { category })
  return (
    <div
      role="status"
      data-testid="ticket-panel-suggestion"
      data-kind={hint.kind}
      className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[13px]"
      style={{ background: ACCENT_TINT, borderColor: ACCENT_BORDER }}
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: 'var(--theme-interactive-text, var(--theme-interactive-color))' }} aria-hidden />
      <span className="min-w-0 flex-1 text-[var(--text-color)]">
        {text}
        {' · '}
        <button
          type="button"
          onClick={onApply}
          className={`rounded font-medium underline underline-offset-2 ${FOCUS_RING}`}
          style={{ color: 'var(--theme-interactive-text, var(--theme-interactive-color))' }}
          data-testid="ticket-panel-suggestion-apply"
        >
          {t('ticketPanel.suggestion.apply')}
        </button>
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('ticketPanel.suggestion.dismiss')}
        title={t('ticketPanel.suggestion.dismiss')}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[var(--text-secondary)] hover:bg-[var(--menu-item-hover)] hover:text-[var(--text-color)] ${FOCUS_RING}`}
        data-testid="ticket-panel-suggestion-dismiss"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  )
}
